import { differenceInDays } from 'date-fns';
import { MultiLineChartDataPoint, MultiLineChartSeries, LegendItem } from '../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { GroupedChartData, TimeSeriesGroupingHelper } from '../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { ComplianceTimeSeriesPoint } from '../models/dashboard.model';

const COMPLIANT_COLOR = '#4CAF50';
const NON_COMPLIANT_COLOR = '#FF7F00';

export interface ComplianceChart {
  data: MultiLineChartDataPoint[];
  series: MultiLineChartSeries[];
  legend: LegendItem[];
}

export type ComplianceGrouping = 'hourly' | 'daily' | 'weekly' | 'monthly';

export function getComplianceGrouping(startDate: Date, endDate: Date, maxDailyDays: number): ComplianceGrouping {
  const daysDifference = differenceInDays(endDate, startDate);
  if (daysDifference === 0) return 'hourly';
  if (daysDifference <= maxDailyDays) return 'daily';
  if (daysDifference <= 92) return 'weekly';
  return 'monthly';
}

export function buildComplianceChart(
  points: ComplianceTimeSeriesPoint[],
  startDate: Date,
  endDate: Date,
  grouping: ComplianceGrouping,
  translate: (key: string) => string,
  individualHours: boolean = false
): ComplianceChart {
  if (points.length === 0) return { data: [], series: [], legend: [] };

  const series: MultiLineChartSeries[] = [
    { key: 'compliant', color: COMPLIANT_COLOR, label: translate('maintenanceDashboard.charts.compliant'), showDots: false },
    { key: 'nonCompliant', color: NON_COMPLIANT_COLOR, label: translate('maintenanceDashboard.charts.nonCompliant'), showDots: false }
  ];

  const valueByKey: Record<string, (point: ComplianceTimeSeriesPoint) => number> = {
    compliant: point => point.compliantAssets ?? 0,
    nonCompliant: point => point.nonCompliantAssets ?? 0
  };

  const datasets = series.flatMap(s => {
    const getValue = valueByKey[s.key];
    return [
      {
        label: s.key,
        data: points.map(point => ({ totalValue: getValue(point), timeSeries: new Date(point.timeSeries) }))
      },
      {
        label: `${s.key}Count`,
        data: points.map(point => ({ totalValue: 1, timeSeries: new Date(point.timeSeries) }))
      }
    ];
  });

  const groupedData = groupDatasets(datasets, startDate, endDate, grouping, individualHours);

  const data = groupedData.map(item => {
    const row: MultiLineChartDataPoint = { x: item.label };
    series.forEach(s => {
      const count = Number(item[`${s.key}Count`]) || 0;
      row[s.key] = count > 0 ? Math.round((Number(item[s.key]) || 0) / count) : 0;
    });
    return row;
  });

  return {
    data,
    series,
    legend: series.map(s => ({ legend: s.label!, color: s.color }))
  };
}

function groupDatasets(
  datasets: { label: string; data: { totalValue: number; timeSeries: Date }[] }[],
  startDate: Date,
  endDate: Date,
  grouping: ComplianceGrouping,
  individualHours: boolean
): GroupedChartData[] {
  switch (grouping) {
    case 'hourly':
      return TimeSeriesGroupingHelper.groupByHours(datasets, individualHours);
    case 'daily':
      return TimeSeriesGroupingHelper.groupByDays(datasets, startDate, endDate);
    case 'weekly':
      return TimeSeriesGroupingHelper.groupByWeeks(datasets, startDate, endDate);
    default:
      return TimeSeriesGroupingHelper.groupByMonths(datasets, startDate, endDate);
  }
}
